import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Topbar from "../Topbar";
import { num, shortDate } from "./listsModel.mjs";
import { BuiltIn, Icon, NewListModal, useLists } from "./parts";
import "./lists.css";

/*
 * THE LISTS INDEX (frame 1). Audiences first, then the one Block list, which is
 * hatched rather than red: red is for warnings, and the hatch already means
 * "here but not in play" on the board's on-hold cards. The review banner leads
 * while any name-only joins are left to check.
 */
export default function ListsIndexPage() {
  const [{ lists, peopleOnAny, reviewCount, loaded }, reload] = useLists();
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const audiences = lists.filter((l) => l.kind === "standard");
  const block = lists.find((l) => l.kind === "suppression");

  return (
    <>
      <Topbar title="Lists" sub={loaded ? `${lists.length} lists · ${num(peopleOnAny)} people on at least one` : ""}>
        <button className="crm-btn crm-btn--primary" type="button" onClick={() => setCreating(true)}>
          <Icon.plus />New list
        </button>
      </Topbar>

      <div className="lst-page lst-page--index">
        {reviewCount > 0 && (
          <div className="lst-banner">
            <div className="lst-banner__icon"><Icon.pair /></div>
            <div style={{ flex: "1 1 360px", minWidth: 0 }}>
              <div className="lst-banner__title">{num(reviewCount)} {reviewCount === 1 ? "person was" : "people were"} joined by name across companies</div>
              <div className="lst-sub" style={{ marginTop: 2 }}>
                Same name, different company, no shared LinkedIn or email. Check them before you weed: a wrong join puts two people on one row.
              </div>
            </div>
            <Link className="crm-btn crm-btn--secondary" to="/lists/check">Check them</Link>
          </div>
        )}

        <div className="lst-stack">
          <h2 className="lst-sect">AUDIENCES</h2>
          {audiences.map((l) => (
            <button key={l.id} type="button" className="lst-row" onClick={() => navigate(`/lists/${l.slug || l.id}`)}>
              <div className="lst-row__body">
                <div className="lst-row__name">{l.name} {l.system && <BuiltIn />}</div>
                {l.description && <div className="lst-row__desc">{l.description}</div>}
                <div className="lst-row__rule">
                  {l.rule ? (
                    <><span className="lst-pill lst-pill--k">Rule</span>{(l.rule_summary || []).join(" · ")}</>
                  ) : (
                    <><span className="lst-pill">By hand</span>made by you · {shortDate(l.created_at)}</>
                  )}
                </div>
              </div>
              <div className="lst-row__count">
                <div className="lst-row__num crm-num">{num(l.member_count)}</div>
                <div className="lst-row__meta">
                  {l.member_count === 1 ? "person" : "people"}
                  {l.rule && l.removed_count > 0 ? ` · ${num(l.removed_count)} removed by hand` : ""}
                  {!l.rule && l.added_week > 0 ? ` · ${num(l.added_week)} added this week` : ""}
                </div>
              </div>
            </button>
          ))}
          {loaded && audiences.length === 0 && <div className="lst-sub">No lists yet.</div>}
        </div>

        {block && (
          <div className="lst-stack">
            <h2 className="lst-sect">BLOCK</h2>
            <button type="button" className="lst-row lst-hatch" onClick={() => navigate(`/lists/${block.slug || block.id}`)}>
              <div className="lst-block-icon"><Icon.ban size={22} /></div>
              <div className="lst-row__body">
                <div className="lst-row__name"><span className="lst-onwhite">{block.name}</span> <BuiltIn /></div>
                <div className="lst-row__desc lst-onwhite" style={{ color: "var(--crm-black)", display: "inline-block", padding: "2px 4px", marginTop: 6 }}>
                  Outranks every list. Adding someone here takes them off all other lists; taking them off puts them back. Automations can't add them anywhere.
                </div>
              </div>
              <div className="lst-row__count">
                <div className="lst-row__num crm-num lst-onwhite" style={{ display: "inline-block" }}>{num(block.member_count)}</div>
                <div><span className="lst-row__meta lst-onwhite" style={{ display: "inline-block", color: "var(--crm-black)" }}>
                  {block.member_count ? (block.member_count === 1 ? "person blocked" : "people blocked") : "nobody blocked yet"}
                </span></div>
              </div>
            </button>
          </div>
        )}
      </div>

      {creating && (
        <NewListModal
          onClose={() => setCreating(false)}
          onCreated={async (list) => {
            setCreating(false);
            await reload();
            navigate(`/lists/${list.slug || list.id}`);
          }}
        />
      )}
    </>
  );
}
